/**
 * Accounts: every credential that can sign in, and the beheerder's actions on one - the inloglink
 * and the link to a persoon. The koppeling wording itself lives in `koppelingen`, which the
 * persoon zoeker shares with the ondersteuners screen.
 * See docs/adr/0018-identity-linking.md, docs/adr/0020-local-accounts.md and
 * docs/adr/0066-an-inloglink-is-the-fourth-door-and-a-password-becomes-optional.md.
 */
export const lokaleAccounts = {
  uitnodigen: {
    title: "Account aanmaken",
    uitleg:
      "Vul het e-mailadres in. Hoort dat adres bij precies één persoon in het JBNL Portaal? Dan koppelt de Wedstrijdplanner het account meteen. Zo niet, dan kies je zelf wie het is.",
    email: "E-mailadres",
    submit: "Inloglink sturen",
    automatischGekoppeld:
      "De inloglink is verstuurd en het account is automatisch gekoppeld.",
    verstuurd: "De inloglink is verstuurd.",
    /** Candidates, never a proposal: ADR-0018 declined to trust an ambiguous address. */
    geenKandidaten:
      "Geen enkele persoon in het JBNL Portaal heeft dit e-mailadres. Zoek de persoon op naam, of laat het adres eerst in het JBNL Portaal verbeteren.",
    meerdereKandidaten:
      "Meerdere personen hebben dit e-mailadres. Kies wie dit account krijgt.",
    kandidaat: "{{naam}} - {{email}}",
    kies: "Kies",
  },

  lijst: {
    label: "Accounts",
    naam: "Naam",
    deur: "Inlogmethode",
    status: "Status",
    filterDeur: "Inlogmethode",
    filterStatus: "Status",
    zoek: "Zoek op naam of e-mailadres",
    /** Beside the status, because Actief says somebody signed in - not that a link is open. */
    inloglinkOpen: "inloglink open",
    /**
     * Under the status rather than in a column of their own, so that the unfiltered list shows
     * what the filter strip counts.
     */
    koppelingWacht: "wacht op koppeling",
    /** An account issued here has no provider subject; the naam column says so rather than lying. */
    geenGebruiker: "geen aanbieder",
    koppelingAfwijkend: "afwijkend adres",
    /** Per filter, because "er zijn nog geen accounts" is wrong when two of the three are empty. */
    leeg: {
      alle: "Er zijn nog geen accounts.",
      wacht: "Er wacht niets op een koppeling.",
      afwijkend: "Geen enkele koppeling is als afwijkend gemarkeerd.",
    },
    terug: "Alle accounts",
    kies: "Kies een account om het te beheren.",
  },

  /**
   * The strip above the list. "Wacht op koppeling" and "Afwijkend adres" were a screen of their own
   * - Koppelingen - over this same table. They stay reachable as a set because ADR-0018 flags a
   * known-wrong link so that somebody checks it.
   */
  filter: {
    label: "Welke accounts",
    alle: "Alle accounts",
    wacht: "Wacht op koppeling",
    afwijkend: "Afwijkend adres",
  },

  /** Which system holds the address a link would go to. */
  bron: {
    portaal: "JBNL Portaal",
    vbs: "VBS",
  },

  /** Which login methode an account signs in with. */
  deur: {
    provider: "Microsoft",
    inloglink: "Alleen inloglink",
    wachtwoord: "Wachtwoord",
  },

  detail: {
    /**
     * The card title for an account with no persoon yet, in place of the naam it claims: on an
     * open door whoever signed in chose that naam, and a heading reads as identity (ADR-0018).
     */
    accountTitel: "Account",
    status: "Status",
    deur: "Inlogmethode",
    adresUit: "Adres uit",
    laatstVerstuurd: "Laatste inloglink",
    laatstGebruikt: "Laatst ingelogd",
    nooit: "nooit",
    adresUitleg:
      "De inloglink gaat altijd naar het adres dat JBNL van deze persoon heeft. Klopt dat adres niet? Laat het dan aanpassen in het JBNL Portaal, want in de backoffice kan dat niet.",
    /** An ondersteuner's address comes from VBS, so Portaal is the wrong place to send them. */
    adresUitlegVbs:
      "De inloglink gaat altijd naar het adres dat JBNL van deze persoon heeft. Dit adres komt uit VBS. Klopt het adres niet? Laat het dan aanpassen in VBS, want in de backoffice kan dat niet.",
    uitstaand: "Er staat nog een inloglink open, geldig tot {{datum}}.",
    verstuurd: "De inloglink is verstuurd.",
    ingetrokken: "De uitstaande inloglinks zijn ingetrokken.",
    gereset:
      "Het wachtwoord en de tweede factor zijn weggehaald en er is een inloglink verstuurd.",
    stuurInloglink: "Inloglink sturen",
    trekIn: "Uitstaande inloglinks intrekken",
    resetCredential: "Wachtwoord en tweede factor weghalen",
    resetUitleg:
      "Haal het wachtwoord en de tweede factor weg als iemand de authenticator-app kwijt is. De koppeling met de persoon blijft staan. Die persoon logt daarna in met een inloglink en kan zelf een nieuw wachtwoord instellen.",
  },

  /** Whether the persoon behind a beheerder account may change things or only read (ADR-0078). */
  rechten: {
    title: "Rechten",
    label: "Mag wijzigen",
    uitleg:
      "Een beheerder ziet alles in de backoffice. Staat Mag wijzigen aan? Dan kan de beheerder ook dingen aanpassen. Staat Mag wijzigen uit? Dan kan de beheerder alleen lezen.",
    geenRecht: "Alleen een beheerder die mag wijzigen, kan rechten aanpassen.",
    geenBeheerder:
      "Deze persoon is in het JBNL Portaal geen beheerder. Je kunt hier daarom geen rechten instellen.",
  },

  verwijderen: {
    knop: "Account verwijderen",
    vraag: "Account verwijderen?",
    beschrijving:
      "Met dit account kan niemand meer inloggen. De persoon zelf blijft staan. Logt iemand opnieuw in via dezelfde aanbieder? Dan komt er een nieuw account, dat op een koppeling wacht.",
    bevestig: "Ja, verwijderen",
    geenRecht:
      "Alleen een beheerder die mag wijzigen, kan een account verwijderen.",
  },

  /** The section in the detail card that says who this account is. */
  koppeling: {
    title: "Koppeling",
  },

  /** JBNL's wording for each state the API reports, keyed on the API's own value. */
  status: {
    actief: "Actief",
    "nooit-aangemeld": "Nooit ingelogd",
    "inloglink-verstuurd": "Inloglink verstuurd",
    "inloglink-verlopen": "Inloglink verlopen",
    "tweede-factor-geblokkeerd": "Geblokkeerd op tweede factor",
    geblokkeerd: "Geblokkeerd",
  },

  errors: {
    ophalen: "Kon de accounts niet ophalen ({{status}}).",
    aanmaken: "Aanmaken is mislukt ({{status}}).",
    versturen: "Versturen is mislukt ({{status}}).",
    resetten: "Weghalen is mislukt ({{status}}).",
    intrekken: "Intrekken is mislukt ({{status}}).",
    rechten: "Rechten aanpassen is mislukt ({{status}}).",
    verwijderen: "Verwijderen is mislukt ({{status}}).",
  },
} as const;
